import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ArrowClockwise, MagnifyingGlass, Calendar, MapPin, Laptop, UserCircle, CaretLeft, CaretRight, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginLogs({ initialHandlerId, onBack }) {
  const [logs, setLogs] = useState([]);
  const [handlers, setHandlers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedHandlerId, setSelectedHandlerId] = useState(initialHandlerId || 'all');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 15; // 15 records per page

  // Fetch all handlers for the filter dropdown
  useEffect(() => {
    const fetchHandlers = async () => {
      try {
        const res = await axios.get(`${API}/users/handlers`);
        setHandlers(res.data?.handlers || res.data || []);
      } catch (err) {
        console.error('Failed to fetch handlers:', err);
      }
    };
    fetchHandlers();
  }, []);

  // Sync initialHandlerId prop with selectedHandlerId state when prop changes
  useEffect(() => {
    if (initialHandlerId) {
      setSelectedHandlerId(initialHandlerId);
      setCurrentPage(1);
    }
  }, [initialHandlerId]);

  // Debounce search query to prevent too many API requests
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Main fetch function
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const handlerParam = selectedHandlerId !== 'all' ? selectedHandlerId : '';
      const res = await axios.get(`${API}/users/login-logs`, {
        params: {
          page: currentPage,
          limit,
          handlerId: handlerParam,
          search: debouncedSearch
        }
      });
      setLogs(res.data?.logs || []);
      setTotalPages(res.data?.totalPages || 1);
      setTotalRecords(res.data?.total || 0);
    } catch (err) {
      console.error('Failed to fetch login logs:', err);
      toast.error('Failed to load login logs');
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedHandlerId, debouncedSearch]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const getBrowserOS = (userAgent) => {
    if (!userAgent) return 'Unknown Client';
    
    let os = 'Unknown OS';
    let browser = 'Unknown Browser';

    if (userAgent.includes('Windows')) os = 'Windows';
    else if (userAgent.includes('Macintosh')) os = 'macOS';
    else if (userAgent.includes('Linux')) os = 'Linux';
    else if (userAgent.includes('Android')) os = 'Android';
    else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) os = 'iOS';

    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) browser = 'Safari';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Edg')) browser = 'Edge';

    return `${browser} on ${os}`;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleHandlerFilterChange = (e) => {
    setSelectedHandlerId(e.target.value);
    setCurrentPage(1);
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxPageButtons = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

    if (endPage - startPage < maxPageButtons - 1) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-semibold transition-all cursor-pointer ${
            currentPage === i
              ? 'bg-green-500 text-white shadow-sm'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
          }`}
        >
          {i}
        </button>
      );
    }
    return buttons;
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-150 p-6 max-w-6xl mx-auto space-y-6">
      {/* Header and Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-5">
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors cursor-pointer text-gray-600"
              title="Back to Handler list"
            >
              <ArrowLeft size={20} weight="bold" />
            </button>
          )}
          <div>
            <h2 className="text-xl font-bold text-gray-800">Login Logs</h2>
            <p className="text-sm text-gray-500 mt-1">
              Real-time tracking of handler & admin logins, active IPs, and locations.
            </p>
          </div>
        </div>
        
        <button
          onClick={fetchLogs}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 border rounded-xl hover:bg-gray-50 text-gray-700 text-sm font-semibold transition-colors disabled:opacity-50 cursor-pointer self-start sm:self-center"
        >
          <ArrowClockwise size={18} className={loading ? 'animate-spin' : ''} />
          Refresh Logs
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search by username, IP, or location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-gray-50/50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all"
          />
        </div>

        {/* Handler & Admin Select Filter */}
        <div className="w-full md:w-64">
          <select
            value={selectedHandlerId}
            onChange={handleHandlerFilterChange}
            className="w-full px-4 py-2.5 bg-gray-50/50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 bg-white cursor-pointer font-medium"
          >
            <option value="all">All Handlers / Admins</option>
            <option value="admins">-- All Admins --</option>
            <option value="handlers">-- All Handlers --</option>
            {handlers.filter(h => h.role === 'super_admin' || h.role === 'admin').length > 0 && (
              <optgroup label="Admins">
                {handlers.filter(h => h.role === 'super_admin' || h.role === 'admin').map(h => (
                  <option key={h.id} value={h.id}>
                    {h.username} (ADMIN)
                  </option>
                ))}
              </optgroup>
            )}
            {handlers.filter(h => h.role !== 'super_admin' && h.role !== 'admin').length > 0 && (
              <optgroup label="Handlers">
                {handlers.filter(h => h.role !== 'super_admin' && h.role !== 'admin').map(h => (
                  <option key={h.id} value={h.id}>
                    {h.username} ({h.role.replace('_', ' ').toUpperCase()})
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
      </div>

      {/* Logs Table / List */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading login logs...</div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-gray-400">No login logs found matching the filters.</div>
      ) : (
        <>
          <div className="overflow-x-auto border border-gray-150 rounded-2xl">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-150 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  <th className="px-6 py-4">User</th>
                  <th className="px-6 py-4">Role</th>
                  <th className="px-6 py-4">IP Address</th>
                  <th className="px-6 py-4">Location</th>
                  <th className="px-6 py-4">Device / Client</th>
                  <th className="px-6 py-4 text-right">Login Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50/50 transition-colors">
                    {/* Avatar + Username */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {log.profile_pic ? (
                          <img
                            src={log.profile_pic}
                            alt={log.username}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <UserCircle size={32} weight="fill" className="text-gray-400" />
                        )}
                        <span className="font-bold text-gray-800">{log.username}</span>
                      </div>
                    </td>

                    {/* Role Badge */}
                    <td className="px-6 py-4">
                      <span className={`inline-block px-2.5 py-0.5 border text-xs font-semibold rounded-full uppercase tracking-wider ${
                        log.role === 'super_admin' || log.role === 'admin'
                          ? 'bg-purple-50 text-purple-700 border-purple-200 font-bold'
                          : 'bg-green-50 text-green-700 border-green-150'
                      }`}>
                        {log.role === 'super_admin' ? 'ADMIN' : log.role.replace('_', ' ')}
                      </span>
                    </td>

                    {/* IP Address */}
                    <td className="px-6 py-4 font-mono text-xs text-gray-600">
                      {log.ip_address || 'N/A'}
                    </td>

                    {/* Location */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5">
                          <MapPin size={16} className="text-gray-400" />
                          <span className="text-xs font-medium">{log.location || 'Unknown'}</span>
                        </div>
                        {log.distance_from_prev_km && Number(log.distance_from_prev_km) > 0 && (
                          <span className="inline-block px-2 py-0.5 text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded-md">
                            ⚠️ {Number(log.distance_from_prev_km).toFixed(1)} km from previous login location
                          </span>
                        )}
                      </div>
                    </td>

                    {/* User Agent / OS */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5">
                        <Laptop size={16} className="text-gray-400" />
                        <span className="text-xs truncate max-w-[200px]" title={log.user_agent}>
                          {getBrowserOS(log.user_agent)}
                        </span>
                      </div>
                    </td>

                    {/* Login Time */}
                    <td className="px-6 py-4 text-right">
                      <div className="flex flex-col items-end gap-0.5">
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Calendar size={14} />
                          <span>
                            {new Date(log.login_time).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                        <span className="text-xs font-semibold text-gray-700">
                          {new Date(log.login_time).toLocaleTimeString(undefined, {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-100 pt-4">
            <span className="text-xs text-gray-500 font-medium">
              Showing <span className="font-semibold text-gray-700">{logs.length}</span> of{' '}
              <span className="font-semibold text-gray-700">{totalRecords}</span> entries
            </span>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:hover:bg-transparent cursor-pointer"
              >
                <CaretLeft size={16} weight="bold" />
              </button>

              {renderPaginationButtons()}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:hover:bg-transparent cursor-pointer"
              >
                <CaretRight size={16} weight="bold" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
